import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Calendar, Clock, Home, User, Camera, FileText, Send,
  CheckCircle, AlertCircle, Loader2, Plus, Mail, Phone,
  CalendarDays, ClipboardCheck, Image, RefreshCw, CheckSquare, Square,
  Eye, Download, List
} from "lucide-react";
import { API } from "@/App";

export const InventoryDashboard = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [activeTab, setActiveTab] = useState("today");
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [bookingData, setBookingData] = useState({
    proposed_dates: [],
    proposed_times: [],
    notes: "",
    inspection_type: "periodic"
  });
  const [bulkBookingData, setBulkBookingData] = useState({
    proposed_dates: [],
    proposed_times: ["09:00", "10:00", "14:00", "15:00"],
    notes: "",
    inspection_type: "periodic"
  });
  const [sending, setSending] = useState(false);
  const [bulkSending, setBulkSending] = useState(false);
  const [properties, setProperties] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [hiveStatus, setHiveStatus] = useState(null);
  const [completedReports, setCompletedReports] = useState([]);
  const [completedLoading, setCompletedLoading] = useState(false);
  const [completedFilter, setCompletedFilter] = useState("all");
  const [completedSearch, setCompletedSearch] = useState("");

  useEffect(() => {
    fetchDashboard();
    fetchProperties();
    fetchHiveStatus();
    fetchCompletedReports();
  }, []);

  const fetchDashboard = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/inventory/dashboard`, { headers });
      setDashboard(response.data);
    } catch (error) {
      console.error("Error fetching dashboard:", error);
      if (error.response?.status === 403) {
        toast.error("Access denied - Inventory Clerk role required");
        navigate("/admin");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchHiveStatus = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/inventory/hive/status`, { headers });
      setHiveStatus(response.data);
    } catch (error) {
      console.error("Error fetching InventoryHive status:", error);
    }
  };

  const fetchProperties = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/lettings/properties`, { headers });
      setProperties(response.data.properties || []);
    } catch (error) {
      console.error("Error fetching properties:", error);
    }
  };

  const fetchCompletedReports = async () => {
    setCompletedLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/inventory-reports/completed`, { headers });
      setCompletedReports(response.data.reports || []);
    } catch (error) {
      console.error("Error fetching completed reports:", error);
    } finally {
      setCompletedLoading(false);
    }
  };

  const openBookingModal = (property) => {
    setSelectedProperty(property);
    setBookingData({
      proposed_dates: [],
      proposed_times: ["09:00", "10:00", "14:00", "15:00"],
      notes: "",
      inspection_type: property.next_inspection_type || "periodic"
    });
    setShowBookingModal(true);
  };

  const addProposedDate = (date) => {
    if (!bookingData.proposed_dates.includes(date)) {
      setBookingData({
        ...bookingData,
        proposed_dates: [...bookingData.proposed_dates, date].sort()
      });
    }
  };

  const removeProposedDate = (date) => {
    setBookingData({
      ...bookingData,
      proposed_dates: bookingData.proposed_dates.filter(d => d !== date)
    });
  };

  const sendBookingRequest = async () => {
    if (!selectedProperty || bookingData.proposed_dates.length === 0) {
      toast.error("Please select at least one proposed date");
      return;
    }

    setSending(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/inventory/send-booking-request`, {
        property_id: selectedProperty.property_id,
        inspection_type: bookingData.inspection_type,
        proposed_dates: bookingData.proposed_dates,
        proposed_times: bookingData.proposed_times,
        notes: bookingData.notes
      }, { headers });
      
      toast.success("Booking request sent to tenant");
      setShowBookingModal(false);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send booking request");
    } finally {
      setSending(false);
    }
  };

  const confirmBooking = async (bookingId, date, time) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/inventory/confirm-booking/${bookingId}`, {
        confirmed_date: date,
        confirmed_time: time
      }, { headers });
      
      toast.success("Booking confirmed");
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to confirm booking");
    }
  };

  // Bulk selection functions
  const togglePropertySelection = (prop) => {
    setSelectedProperties(prev => {
      const isSelected = prev.some(p => p.property_id === prop.property_id);
      if (isSelected) {
        return prev.filter(p => p.property_id !== prop.property_id);
      } else {
        return [...prev, prop];
      }
    });
  };

  const selectAllFiltered = () => {
    const currentIds = selectedProperties.map(p => p.property_id);
    const toAdd = filteredProperties.slice(0, 50).filter(p => !currentIds.includes(p.property_id));
    setSelectedProperties([...selectedProperties, ...toAdd]);
  };

  const clearSelection = () => {
    setSelectedProperties([]);
  };

  const openBulkModal = () => {
    if (selectedProperties.length === 0) {
      toast.error("Please select at least one property");
      return;
    }
    setBulkBookingData({
      proposed_dates: [],
      proposed_times: ["09:00", "10:00", "14:00", "15:00"],
      notes: "",
      inspection_type: "periodic"
    });
    setShowBulkModal(true);
  };

  const addBulkProposedDate = (date) => {
    if (date && !bulkBookingData.proposed_dates.includes(date)) {
      setBulkBookingData({
        ...bulkBookingData,
        proposed_dates: [...bulkBookingData.proposed_dates, date].sort()
      });
    }
  };

  const removeBulkProposedDate = (date) => {
    setBulkBookingData({
      ...bulkBookingData,
      proposed_dates: bulkBookingData.proposed_dates.filter(d => d !== date)
    });
  };

  const sendBulkBookingRequests = async () => {
    if (selectedProperties.length === 0 || bulkBookingData.proposed_dates.length === 0) {
      toast.error("Please select properties and at least one proposed date");
      return;
    }

    setBulkSending(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${API}/inventory/bulk-booking-request`, {
        property_ids: selectedProperties.map(p => p.property_id),
        inspection_type: bulkBookingData.inspection_type,
        proposed_dates: bulkBookingData.proposed_dates,
        proposed_times: bulkBookingData.proposed_times,
        notes: bulkBookingData.notes
      }, { headers });
      
      const { sent_count, skipped_count, errors } = response.data;
      if (sent_count > 0) {
        toast.success(`Sent ${sent_count} booking requests successfully`);
      }
      if (skipped_count > 0) {
        toast.warning(`${skipped_count} properties skipped (no active tenant or missing email)`);
      }
      
      setShowBulkModal(false);
      setSelectedProperties([]);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send bulk booking requests");
    } finally {
      setBulkSending(false);
    }
  };

  const filteredProperties = properties.filter(p => 
    p.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.postcode?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-700 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">Inventory Dashboard</h1>
              <p className="text-purple-200">Manage inspections & bookings</p>
            </div>
            <div className="flex items-center gap-3">
              {/* InventoryHive Status */}
              {hiveStatus && (
                <div className={`px-3 py-1 rounded-full text-sm flex items-center gap-2 ${
                  hiveStatus.connected 
                    ? 'bg-green-500/20 text-green-100' 
                    : 'bg-red-500/20 text-red-100'
                }`}>
                  <div className={`w-2 h-2 rounded-full ${hiveStatus.connected ? 'bg-green-400' : 'bg-red-400'}`} />
                  InventoryHive {hiveStatus.connected ? 'Connected' : 'Not Connected'}
                </div>
              )}
              <Button 
                variant="secondary" 
                onClick={() => setShowBookingModal(true)}
                className="bg-white text-purple-700 hover:bg-purple-50"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Booking
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Stats Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <Card className="bg-white shadow-lg">
            <CardContent className="p-4 text-center">
              <CalendarDays className="w-8 h-8 mx-auto text-blue-600 mb-2" />
              <p className="text-2xl font-bold">{dashboard?.stats?.today_count || 0}</p>
              <p className="text-xs text-muted-foreground">Today</p>
            </CardContent>
          </Card>
          <Card className="bg-white shadow-lg">
            <CardContent className="p-4 text-center">
              <Calendar className="w-8 h-8 mx-auto text-green-600 mb-2" />
              <p className="text-2xl font-bold">{dashboard?.stats?.this_week_count || 0}</p>
              <p className="text-xs text-muted-foreground">This Week</p>
            </CardContent>
          </Card>
          <Card className="bg-white shadow-lg">
            <CardContent className="p-4 text-center">
              <Mail className="w-8 h-8 mx-auto text-amber-600 mb-2" />
              <p className="text-2xl font-bold">{dashboard?.stats?.pending_requests || 0}</p>
              <p className="text-xs text-muted-foreground">Pending Responses</p>
            </CardContent>
          </Card>
          <Card className="bg-white shadow-lg">
            <CardContent className="p-4 text-center">
              <FileText className="w-8 h-8 mx-auto text-red-600 mb-2" />
              <p className="text-2xl font-bold">{dashboard?.stats?.needs_report || 0}</p>
              <p className="text-xs text-muted-foreground">Need Reports</p>
            </CardContent>
          </Card>
          <Card className="bg-white shadow-lg">
            <CardContent className="p-4 text-center">
              <ClipboardCheck className="w-8 h-8 mx-auto text-purple-600 mb-2" />
              <p className="text-2xl font-bold">{dashboard?.stats?.upcoming_30_days || 0}</p>
              <p className="text-xs text-muted-foreground">Next 30 Days</p>
            </CardContent>
          </Card>
          <Card className="bg-white shadow-lg cursor-pointer hover:shadow-xl transition-shadow" onClick={() => setActiveTab("completed")} data-testid="completed-inventories-card">
            <CardContent className="p-4 text-center">
              <CheckCircle className="w-8 h-8 mx-auto text-emerald-600 mb-2" />
              <p className="text-2xl font-bold">{completedReports.length}</p>
              <p className="text-xs text-muted-foreground">Completed</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="today">Today&apos;s Inspections</TabsTrigger>
            <TabsTrigger value="week">This Week</TabsTrigger>
            <TabsTrigger value="pending">Pending Bookings</TabsTrigger>
            <TabsTrigger value="reports">Need Reports</TabsTrigger>
            <TabsTrigger value="completed" data-testid="tab-completed">
              <CheckCircle className="w-4 h-4 mr-1" />
              Completed ({completedReports.length})
            </TabsTrigger>
            <TabsTrigger value="schedule">Schedule New</TabsTrigger>
          </TabsList>

          {/* Today's Inspections */}
          <TabsContent value="today">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CalendarDays className="w-5 h-5 text-blue-600" />
                  Today&apos;s Inspections
                </CardTitle>
              </CardHeader>
              <CardContent>
                {dashboard?.todays_inspections?.length > 0 ? (
                  <div className="space-y-3">
                    {dashboard.todays_inspections.map((insp) => (
                      <div key={insp.inspection_id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                            <Home className="w-6 h-6 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium">{insp.property_address}</p>
                            <p className="text-sm text-muted-foreground">
                              {insp.scheduled_time || "Time TBC"} • {insp.inspection_type?.replace('_', ' ')}
                            </p>
                            {insp.tenant_name && (
                              <p className="text-sm text-muted-foreground flex items-center gap-1">
                                <User className="w-3 h-3" /> {insp.tenant_name}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={insp.status === "confirmed" ? "default" : "secondary"}>
                            {insp.status}
                          </Badge>
                          <Button size="sm" variant="outline" onClick={() => navigate(`/admin/lettings/property/${insp.property_id}`)}>
                            View Property
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <CalendarDays className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p>No inspections scheduled for today</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* This Week */}
          <TabsContent value="week">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-green-600" />
                  This Week&apos;s Inspections
                </CardTitle>
              </CardHeader>
              <CardContent>
                {dashboard?.this_week?.length > 0 ? (
                  <div className="space-y-3">
                    {dashboard.this_week.map((insp) => (
                      <div key={insp.inspection_id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border">
                        <div className="flex items-center gap-4">
                          <div className="text-center min-w-[60px]">
                            <p className="text-lg font-bold text-green-600">
                              {new Date(insp.scheduled_date).toLocaleDateString('en-GB', { day: 'numeric' })}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(insp.scheduled_date).toLocaleDateString('en-GB', { weekday: 'short' })}
                            </p>
                          </div>
                          <div>
                            <p className="font-medium">{insp.property_address}</p>
                            <p className="text-sm text-muted-foreground">
                              {insp.scheduled_time || "Time TBC"} • {insp.inspection_type?.replace('_', ' ')}
                            </p>
                          </div>
                        </div>
                        <Badge variant={insp.status === "confirmed" ? "default" : "secondary"}>
                          {insp.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Calendar className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p>No inspections scheduled this week</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pending Bookings */}
          <TabsContent value="pending">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-amber-600" />
                  Pending Tenant Responses
                </CardTitle>
                <CardDescription>Booking requests awaiting tenant confirmation</CardDescription>
              </CardHeader>
              <CardContent>
                {dashboard?.pending_booking_requests?.length > 0 ? (
                  <div className="space-y-3">
                    {dashboard.pending_booking_requests.map((req) => (
                      <div key={req.booking_id} className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium">{req.property_address}</p>
                            <p className="text-sm text-muted-foreground">
                              Tenant: {req.tenant_name} ({req.tenant_email})
                            </p>
                            <p className="text-sm text-muted-foreground">
                              Proposed: {req.proposed_dates?.join(", ")}
                            </p>
                            <Badge variant="secondary" className="mt-2">{req.status?.replace(/_/g, ' ')}</Badge>
                          </div>
                          {req.status === "tenant_confirmed" && (
                            <Button 
                              size="sm"
                              onClick={() => confirmBooking(req.booking_id, req.confirmed_date, req.confirmed_time)}
                            >
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Confirm
                            </Button>
                          )}
                        </div>
                        {req.tenant_proposed_dates && (
                          <div className="mt-3 p-2 bg-white rounded border">
                            <p className="text-sm font-medium">Tenant proposed:</p>
                            <p className="text-sm text-muted-foreground">{req.tenant_proposed_dates.join(", ")}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Mail className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p>No pending booking requests</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Need Reports */}
          <TabsContent value="reports">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-red-600" />
                  Completed - Need Reports
                </CardTitle>
                <CardDescription>Inspections completed but reports not yet sent</CardDescription>
              </CardHeader>
              <CardContent>
                {dashboard?.needs_report?.length > 0 ? (
                  <div className="space-y-3">
                    {dashboard.needs_report.map((insp) => (
                      <div key={insp.inspection_id} className="flex items-center justify-between p-4 bg-red-50 rounded-lg border border-red-200">
                        <div>
                          <p className="font-medium">{insp.property_address}</p>
                          <p className="text-sm text-muted-foreground">
                            Completed: {insp.completed_date} • {insp.inspection_type?.replace('_', ' ')}
                          </p>
                          {insp.photos?.length > 0 && (
                            <Badge variant="outline" className="mt-1">
                              <Camera className="w-3 h-3 mr-1" /> {insp.photos.length} photos
                            </Badge>
                          )}
                        </div>
                        <Button size="sm">
                          <Send className="w-4 h-4 mr-2" />
                          Send Report
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <CheckCircle className="w-12 h-12 mx-auto text-green-300 mb-4" />
                    <p>All reports sent!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Schedule New */}
          <TabsContent value="schedule">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Plus className="w-5 h-5 text-purple-600" />
                      Schedule New Inspection
                    </CardTitle>
                    <CardDescription>Select properties to send booking requests to tenants</CardDescription>
                  </div>
                  {selectedProperties.length > 0 && (
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="text-sm">
                        {selectedProperties.length} selected
                      </Badge>
                      <Button size="sm" variant="outline" onClick={clearSelection}>
                        Clear
                      </Button>
                      <Button size="sm" onClick={openBulkModal} className="bg-purple-600 hover:bg-purple-700">
                        <Send className="w-4 h-4 mr-2" />
                        Send Bulk Request
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2 mb-4">
                  <Input
                    placeholder="Search properties..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="flex-1"
                  />
                  <Button variant="outline" onClick={selectAllFiltered} size="sm">
                    <CheckSquare className="w-4 h-4 mr-2" />
                    Select All
                  </Button>
                </div>
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {filteredProperties.slice(0, 50).map((prop) => {
                    const isSelected = selectedProperties.some(p => p.property_id === prop.property_id);
                    return (
                      <div 
                        key={prop.property_id}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                          isSelected 
                            ? 'bg-purple-50 border-purple-300' 
                            : 'bg-slate-50 hover:bg-slate-100'
                        }`}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => togglePropertySelection(prop)}
                          className="h-5 w-5"
                        />
                        <div 
                          className="flex-1"
                          onClick={() => togglePropertySelection(prop)}
                        >
                          <p className="font-medium">{prop.address}</p>
                          <p className="text-sm text-muted-foreground">{prop.postcode}</p>
                          {prop.next_inspection_date && (
                            <Badge variant="secondary" className="mt-1 text-xs">
                              Due: {prop.next_inspection_date}
                            </Badge>
                          )}
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            openBookingModal(prop);
                          }}
                        >
                          <Calendar className="w-4 h-4 mr-2" />
                          Book
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Completed Inventories */}
          <TabsContent value="completed" data-testid="completed-inventories-tab">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                      Completed Inventories
                    </CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">All completed inventory reports, sorted oldest to newest</p>
                  </div>
                </div>
                {/* Filters */}
                <div className="flex flex-wrap gap-3 mt-4">
                  <Input
                    placeholder="Search by address, tenant..."
                    value={completedSearch}
                    onChange={(e) => setCompletedSearch(e.target.value)}
                    className="max-w-xs"
                    data-testid="completed-search"
                  />
                  <select
                    value={completedFilter}
                    onChange={(e) => setCompletedFilter(e.target.value)}
                    className="border rounded-md px-3 py-2 text-sm"
                    data-testid="completed-filter"
                  >
                    <option value="all">All Types</option>
                    <option value="inventory">Inventory</option>
                    <option value="check_in">Check-In</option>
                    <option value="check_out">Check-Out</option>
                    <option value="interim_inspection">Interim Inspection</option>
                  </select>
                </div>
              </CardHeader>
              <CardContent>
                {completedLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                  </div>
                ) : (() => {
                  // Filter reports
                  const filteredReports = completedReports.filter(report => {
                    const matchesSearch = !completedSearch || 
                      report.property_address?.toLowerCase().includes(completedSearch.toLowerCase()) ||
                      report.tenant_name?.toLowerCase().includes(completedSearch.toLowerCase()) ||
                      report.property_postcode?.toLowerCase().includes(completedSearch.toLowerCase());
                    const matchesType = completedFilter === "all" || report.report_type === completedFilter;
                    return matchesSearch && matchesType;
                  });
                  
                  return filteredReports.length > 0 ? (
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground mb-2">
                      Showing {filteredReports.length} of {completedReports.length} reports
                    </p>
                    {filteredReports.map((report) => (
                      <div 
                        key={report.report_id} 
                        className="flex items-center justify-between p-4 bg-emerald-50 rounded-lg border border-emerald-200 hover:bg-emerald-100 transition-colors cursor-pointer"
                        onClick={() => navigate(`/inventory/report/${report.report_id}`)}
                        data-testid={`completed-report-${report.report_id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                            <Home className="w-6 h-6 text-emerald-600" />
                          </div>
                          <div>
                            <p className="font-medium">{report.property_address}</p>
                            <p className="text-sm text-muted-foreground">
                              {report.report_type_label || report.report_type?.replace('_', ' ')}
                            </p>
                            {report.tenant_name && (
                              <p className="text-sm text-muted-foreground flex items-center gap-1">
                                <User className="w-3 h-3" /> {report.tenant_name}
                              </p>
                            )}
                            {report.tenancy_reference && (
                              <Badge variant="outline" className="mt-1 text-xs">
                                Ref: {report.tenancy_reference}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <Badge className="bg-emerald-100 text-emerald-700">
                            Complete
                          </Badge>
                          <p className="text-xs text-muted-foreground">
                            {report.visit_date && new Date(report.visit_date).toLocaleDateString('en-GB', { 
                              day: 'numeric', 
                              month: 'short', 
                              year: 'numeric' 
                            })}
                          </p>
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/inventory/report/${report.report_id}`);
                              }}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(`${API}/inventory-reports/${report.report_id}/download`, '_blank');
                              }}
                            >
                              <Download className="w-4 h-4 mr-1" />
                              PDF
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <CheckCircle className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p>{completedSearch || completedFilter !== "all" ? "No matching reports found" : "No completed inventories yet"}</p>
                    <p className="text-sm mt-1">{completedSearch || completedFilter !== "all" ? "Try adjusting your filters" : "Completed reports will appear here"}</p>
                  </div>
                );
                })()}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Booking Modal */}
      <Dialog open={showBookingModal} onOpenChange={setShowBookingModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Send Booking Request</DialogTitle>
          </DialogHeader>
          
          {selectedProperty && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium">{selectedProperty.address}</p>
                <p className="text-sm text-muted-foreground">{selectedProperty.postcode}</p>
              </div>
              
              <div>
                <Label>Inspection Type</Label>
                <select
                  className="w-full border rounded-md px-3 py-2 mt-1"
                  value={bookingData.inspection_type}
                  onChange={(e) => setBookingData({ ...bookingData, inspection_type: e.target.value })}
                >
                  <option value="periodic">Periodic Inspection</option>
                  <option value="move_in">Move In</option>
                  <option value="move_out">Move Out / Checkout</option>
                  <option value="inventory">Full Inventory</option>
                </select>
              </div>
              
              <div>
                <Label>Proposed Dates</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="date"
                    onChange={(e) => e.target.value && addProposedDate(e.target.value)}
                  />
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {bookingData.proposed_dates.map((date) => (
                    <Badge 
                      key={date} 
                      variant="secondary" 
                      className="cursor-pointer"
                      onClick={() => removeProposedDate(date)}
                    >
                      {new Date(date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                      <span className="ml-1">×</span>
                    </Badge>
                  ))}
                </div>
              </div>
              
              <div>
                <Label>Notes for Tenant</Label>
                <Textarea
                  value={bookingData.notes}
                  onChange={(e) => setBookingData({ ...bookingData, notes: e.target.value })}
                  placeholder="Any special instructions..."
                  rows={2}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBookingModal(false)}>Cancel</Button>
            <Button onClick={sendBookingRequest} disabled={sending || bookingData.proposed_dates.length === 0}>
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Send to Tenant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Booking Modal */}
      <Dialog open={showBulkModal} onOpenChange={setShowBulkModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckSquare className="w-5 h-5 text-purple-600" />
              Bulk Booking Request
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
              <p className="font-medium text-purple-800">
                {selectedProperties.length} properties selected
              </p>
              <p className="text-sm text-purple-600 mt-1">
                Booking requests will be sent to each tenant individually
              </p>
            </div>

            <div className="max-h-[120px] overflow-y-auto p-2 bg-slate-50 rounded-lg">
              {selectedProperties.map(prop => (
                <div key={prop.property_id} className="text-sm py-1 border-b last:border-0">
                  {prop.address}
                </div>
              ))}
            </div>
            
            <div>
              <Label>Inspection Type</Label>
              <select
                className="w-full border rounded-md px-3 py-2 mt-1"
                value={bulkBookingData.inspection_type}
                onChange={(e) => setBulkBookingData({ ...bulkBookingData, inspection_type: e.target.value })}
              >
                <option value="periodic">Periodic Inspection</option>
                <option value="move_in">Move In</option>
                <option value="move_out">Move Out / Checkout</option>
                <option value="inventory">Full Inventory</option>
              </select>
            </div>
            
            <div>
              <Label>Proposed Dates (same for all properties)</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  type="date"
                  onChange={(e) => e.target.value && addBulkProposedDate(e.target.value)}
                />
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {bulkBookingData.proposed_dates.map((date) => (
                  <Badge 
                    key={date} 
                    variant="secondary" 
                    className="cursor-pointer"
                    onClick={() => removeBulkProposedDate(date)}
                  >
                    {new Date(date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                    <span className="ml-1">×</span>
                  </Badge>
                ))}
              </div>
            </div>
            
            <div>
              <Label>Notes for All Tenants</Label>
              <Textarea
                value={bulkBookingData.notes}
                onChange={(e) => setBulkBookingData({ ...bulkBookingData, notes: e.target.value })}
                placeholder="Any special instructions (optional)..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkModal(false)}>Cancel</Button>
            <Button 
              onClick={sendBulkBookingRequests} 
              disabled={bulkSending || bulkBookingData.proposed_dates.length === 0}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {bulkSending ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send to {selectedProperties.length} Tenants
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InventoryDashboard;
